import { useLayoutEffect, useMemo, useRef } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import Chart, { useChart } from 'src/components/chart';

type MetricKey =
    | 'competitor_visibility_rate'
    | 'top_paid_presence_rate'
    | 'abs_top_paid_presence_rate'
    | 'clicks'
    | 'cost'
    | 'avg_cpc'
    | 'conversions'
    | 'revenue';

type CompetitorVisibility = {
    total: number;
    organic_only: number;
    solo_bidder: number;
    top_ad_position: number;
    other_competitors: number;
};

type AnalysisPoint = {
    date: string;
    competitor_visibility_rate?: CompetitorVisibility;
    paid_top_presence_rate?: number;
    paid_first_presence_rate?: number;
    paid_clicks?: number;
    ad_cost?: number;
    paid_cpc?: number;
    paid_conversions?: number;
    paid_revenue?: number;
    compare?: Omit<AnalysisPoint, 'date' | 'compare'>;
};

type Props = {
    data?: AnalysisPoint[] | { data: AnalysisPoint[] };
    metrics: string[];
    hiddenMetrics?: string[];
    comparison?: 'none' | 'pp' | 'ly';
    granularity?: 'weekly' | 'monthly';
    showLabels?: boolean;
    matchScale?: boolean;
    loading?: boolean;
    onChartMounted?: (instance: any) => void;
};

type ChartDefinition = {
    metric: MetricKey;
    name: string;
    color: string;
    comparisonSeries: boolean;
    categoryField?: keyof CompetitorVisibility;
    type: 'line' | 'bar';
    group?: string;
    data: Array<number | null>;
};

const METRICS: Record<MetricKey, { label: string; color: string; unit: 'percent' | 'currency' | 'number'; type: 'line' | 'bar' }> = {
    competitor_visibility_rate: { label: 'Competitor visibility rate', color: '#9BA5AE', unit: 'percent', type: 'bar' },
    top_paid_presence_rate: { label: 'Paid Top %', color: '#DAA335', unit: 'percent', type: 'line' },
    abs_top_paid_presence_rate: { label: 'Paid First %', color: '#DAA335', unit: 'percent', type: 'line' },
    clicks: { label: 'Paid Clicks', color: '#DAA335', unit: 'number', type: 'bar' },
    cost: { label: 'Ad cost', color: '#DAA335', unit: 'currency', type: 'bar' },
    avg_cpc: { label: 'Paid CPC', color: '#DAA335', unit: 'currency', type: 'line' },
    conversions: { label: 'Paid Conversions', color: '#0052B4', unit: 'number', type: 'line' },
    revenue: { label: 'Paid Revenue', color: '#0052B4', unit: 'currency', type: 'line' },
};

const isMetric = (key: string): key is MetricKey => key in METRICS;

const getValue = (point: Partial<AnalysisPoint> | undefined, metric: MetricKey): number | null => {
    if (!point) return null;
    const raw = metric === 'competitor_visibility_rate'
        ? point.competitor_visibility_rate?.total
        : metric === 'top_paid_presence_rate'
            ? point.paid_top_presence_rate
            : metric === 'abs_top_paid_presence_rate'
                ? point.paid_first_presence_rate
                : metric === 'clicks'
                    ? point.paid_clicks
                    : metric === 'cost'
                        ? point.ad_cost
                        : metric === 'avg_cpc'
                            ? point.paid_cpc
                            : metric === 'conversions'
                                ? point.paid_conversions
                                : point.paid_revenue;
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
};

const formatValue = (value: number | null | undefined, unit: 'percent' | 'currency' | 'number') => {
    if (value == null || !Number.isFinite(value)) return '–';
    const digits = unit === 'number' ? 0 : 2;
    const result = new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(value);
    return unit === 'percent' ? `${result}%` : unit === 'currency' ? `€${result}` : result;
};

const formatChartLabel = (value: number | null | undefined, unit: 'percent' | 'currency' | 'number') => {
    if (value == null || !Number.isFinite(value)) return '';
    if (unit === 'currency' && Math.abs(value) < 10) {
        return `€${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}`;
    }
    const compact = Math.abs(value) >= 1000
        ? `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value / 1000)}K`
        : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
    return unit === 'percent' ? `${compact}%` : unit === 'currency' ? `€${compact}` : compact;
};

const formatDate = (date: string, granularity: 'weekly' | 'monthly') =>
    granularity === 'monthly' ? date.slice(0, 7) : date.slice(0, 10);

const niceAxisMaximum = (maximum: number) => {
    if (maximum <= 0) return 5;
    const roughStep = maximum / 5;
    const magnitude = 10 ** Math.floor(Math.log10(roughStep));
    const normalized = roughStep / magnitude;
    const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
    return step * 5;
};

export default function ConquestModeAnalysisChart({
    data,
    metrics,
    hiddenMetrics = [],
    comparison = 'none',
    granularity = 'weekly',
    showLabels = false,
    matchScale = false,
    loading = false,
    onChartMounted,
}: Props) {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const comparisonContainerRef = useRef<HTMLDivElement>(null);
    const comparisonItemRefs = useRef<Array<HTMLDivElement | null>>([]);
    const points = useMemo(() => {
        const source = Array.isArray(data) ? data : data?.data ?? [];
        return [...source].sort((a, b) => a.date.localeCompare(b.date));
    }, [data]);

    const lastBucketIndex = points.length - 1;
    const lastBucketDate = points[lastBucketIndex]?.date;
    const incompleteBucket = (() => {
        if (!lastBucketDate) return false;
        const date = new Date(`${lastBucketDate.slice(0, 7)}-${lastBucketDate.length === 7 ? '01' : lastBucketDate.slice(8, 10)}T12:00:00`);
        if (Number.isNaN(date.getTime())) return false;
        const today = new Date();
        if (granularity === 'monthly') {
            return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth();
        }
        const monday = (value: Date) => {
            const result = new Date(value.getFullYear(), value.getMonth(), value.getDate());
            result.setDate(result.getDate() - ((result.getDay() + 6) % 7));
            return result.getTime();
        };
        return monday(date) === monday(today);
    })();

    const activeMetrics = useMemo(
        () => metrics.filter(isMetric).filter((key) => !hiddenMetrics.includes(key)),
        [metrics, hiddenMetrics]
    );

    const comparisonValues = useMemo(() => {
        if (comparison === 'none') return [];
        return points.map((point) => activeMetrics.map((metric) => {
            const currentValue = getValue(point, metric);
            const compareValue = getValue(point.compare, metric);
            let evolution: number | null = null;
            if (currentValue !== null && compareValue !== null) {
                evolution = compareValue !== 0
                    ? ((currentValue - compareValue) / Math.abs(compareValue)) * 100
                    : currentValue === 0 ? 0 : null;
            }
            return { metric, color: METRICS[metric].color, value: evolution };
        }));
    }, [activeMetrics, comparison, points]);

    const comparisonPeriodLabel = useMemo(() => {
        if (comparison === 'none' || points.length === 0) return '';
        const parseDate = (value: string) => new Date(`${value}T00:00:00Z`);
        const first = parseDate(points[0].date);
        const last = parseDate(points[points.length - 1].date);
        if (comparison === 'ly') {
            first.setUTCFullYear(first.getUTCFullYear() - 1);
            last.setUTCFullYear(last.getUTCFullYear() - 1);
        } else if (granularity === 'monthly') {
            first.setUTCMonth(first.getUTCMonth() - points.length);
            last.setUTCMonth(last.getUTCMonth() - points.length);
        } else {
            first.setUTCDate(first.getUTCDate() - 7 * points.length);
            last.setUTCDate(last.getUTCDate() - 7 * points.length);
        }
        return `${first.toISOString().slice(0, 10)} -\n${last.toISOString().slice(0, 10)}`;
    }, [comparison, granularity, points]);

    useLayoutEffect(() => {
        if (comparison === 'none' || !comparisonValues.length) return undefined;
        const chartContainer = chartContainerRef.current;
        const comparisonContainer = comparisonContainerRef.current;
        if (!chartContainer || !comparisonContainer) return undefined;
        let frame: number | null = null;
        const updatePositions = () => {
            if (frame !== null) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const labels = chartContainer.querySelectorAll<SVGTextElement>(
                    '.apexcharts-xaxis-texts-g .apexcharts-xaxis-label'
                );
                const parent = comparisonContainer.getBoundingClientRect();
                comparisonItemRefs.current.forEach((element, index) => {
                    if (!element || !labels[index]) return;
                    const rect = labels[index].getBoundingClientRect();
                    element.style.left = `${rect.left + rect.width / 2 - parent.left}px`;
                });
            });
        };
        updatePositions();
        const observer = new ResizeObserver(updatePositions);
        observer.observe(chartContainer);
        observer.observe(comparisonContainer);
        window.addEventListener('resize', updatePositions);
        return () => {
            if (frame !== null) cancelAnimationFrame(frame);
            observer.disconnect();
            window.removeEventListener('resize', updatePositions);
        };
    }, [comparison, comparisonValues, points]);

    const seriesDefinitions = useMemo<ChartDefinition[]>(() => activeMetrics.flatMap((metric): ChartDefinition[] => {
        const option = METRICS[metric];
        if (metric === 'competitor_visibility_rate') {
            const categories: Array<{ field: keyof CompetitorVisibility; label: string; color: string }> = [
                { field: 'organic_only', label: 'Organic', color: '#18A155' },
                { field: 'solo_bidder', label: 'Comp. Solo bidder', color: '#E95954' },
                { field: 'top_ad_position', label: 'Comp. Top ad', color: '#EF8783' },
                { field: 'other_competitors', label: 'Other Comp.', color: '#F7C2C0' },
            ];
            const stacks = categories.map((category) => ({
                metric,
                color: category.color,
                comparisonSeries: false,
                categoryField: category.field,
                name: category.label,
                type: 'bar' as const,
                group: 'competitor-visibility',
                data: points.map((point) => point.competitor_visibility_rate?.[category.field] ?? null),
            }));
            return stacks;
        }
        const current = {
            metric,
            color: option.color,
            comparisonSeries: false,
            categoryField: undefined,
            name: option.label,
            type: option.type,
            data: points.map((point) => getValue(point, metric)),
        };
        return [current];
    }), [activeMetrics, comparison, points]);

    const series = seriesDefinitions.map(({ name, type, data, group }) => ({ name, type, data, ...(group ? { group } : {}) }));
    const seriesMetrics = seriesDefinitions.map((item) => item.metric);
    const colors = seriesDefinitions.map((item) => item.color);

    const axisConfig = useMemo(() => activeMetrics.map((metric, index) => {
        const { unit, label } = METRICS[metric];
        const values = points.flatMap((point) => [
            getValue(point, metric),
            comparison === 'none' ? null : getValue(point.compare, metric),
        ]);
        const maximum = Math.max(0, ...values.filter((value): value is number => value !== null));
        return {
            seriesName: seriesDefinitions.filter((item) => item.metric === metric).map((item) => item.name),
            show: true,
            opposite: index > 0,
            min: 0,
            // Keep a safe upper bound even when Match scale is disabled.
            // Add headroom so peak line markers are not clipped by the plot boundary.
            max: unit === 'percent'
                ? Math.max(100, maximum > 100 ? niceAxisMaximum(maximum * 1.1) : 100)
                : niceAxisMaximum(maximum * (matchScale ? 1.15 : 1.1)),
            tickAmount: 5,
            title: {
                text: `${label} (${unit === 'percent' ? '%' : unit === 'currency' ? '€' : 'number'})`,
                style: { color: '#637381', fontSize: '12px', fontFamily: 'Public Sans, sans-serif', fontWeight: 500 },
            },
            labels: { formatter: (value: number) => formatValue(value, unit) },
        };
    }), [activeMetrics, comparison, matchScale, points, seriesDefinitions]);

    const labelAnnotations: any = showLabels ? seriesDefinitions.flatMap((item, seriesIndex) =>
    (item.metric === 'competitor_visibility_rate' && item.categoryField !== 'organic_only' && item.categoryField !== 'other_competitors' ? [] : item.data.map((value, pointIndex) => {
        if (value == null || !Number.isFinite(value)) return null;
        const metric = seriesMetrics[seriesIndex];
        const unit = METRICS[metric].unit;
        const axisIndex = activeMetrics.indexOf(metric);
        return {
            x: formatDate(points[pointIndex].date, granularity),
            y: item.categoryField === 'other_competitors'
                ? ['organic_only', 'solo_bidder', 'top_ad_position', 'other_competitors'].reduce(
                    (sum, field) => sum + (Number(points[pointIndex].competitor_visibility_rate?.[field as keyof CompetitorVisibility]) || 0), 0
                )
                : value,
            yAxisIndex: axisIndex,
            marker: { size: 0, strokeWidth: 0, strokeColor: 'transparent', fillColor: 'transparent' },
            label: {
                text: `● ${formatChartLabel(value, unit)}`,
                offsetY: item.categoryField === 'other_competitors' ? 16 : item.type === 'bar' ? 7 : -3,
                borderColor: 'rgba(145, 158, 171, 0.20)',
                borderWidth: 1,
                borderRadius: 6,
                style: {
                    background: '#FFFFFF',
                    color: '#1C252E',
                    fontSize: '11px',
                    fontWeight: 500,
                    fontFamily: 'Public Sans, sans-serif',
                    cssClass: `conquest-chart-label conquest-chart-label-${seriesIndex}`,
                },
            },
        };
    }).filter(Boolean))
    ) : [];

    const injectLabelMarkers = () => {
        requestAnimationFrame(() => {
            const container = chartContainerRef.current;
            if (!container) return;

            seriesDefinitions.forEach((item, seriesIndex) => {
                const texts = container.querySelectorAll<SVGTextElement>(
                    `text.conquest-chart-label-${seriesIndex}, .conquest-chart-label-${seriesIndex} text`
                );

                texts.forEach((textElement) => {
                    const svgText = textElement as SVGTextElement;
                    if (svgText.querySelector('.conquest-label-marker')) return;
                    const labelText = svgText.textContent || '';
                    if (!labelText.startsWith('● ')) return;

                    const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
                    tspan.setAttribute('class', 'conquest-label-marker');
                    tspan.setAttribute('fill', item.color);
                    tspan.textContent = '● ';
                    svgText.textContent = labelText.slice(2);
                    svgText.insertBefore(tspan, svgText.firstChild);
                });
            });
        });
    };

    // Même traitement des périodes incomplètes que dans les autres modales
    // Metric Evolution : barres atténuées, dernier segment des lignes pointillé.
    const styleIncompletePeriod = (animateDashes = false) => {
        if (!incompleteBucket || lastBucketIndex < 0) return;
        const container = chartContainerRef.current;
        if (!container) return;

        container.querySelectorAll<SVGElement>(
            `.apexcharts-bar-area[j="${lastBucketIndex}"]`
        ).forEach((bar) => { bar.style.opacity = '0.45'; });

        if (lastBucketIndex < 1) return;

        const reveal = (segment: SVGPathElement) => {
            if (segment.dataset.revealed === 'true') return;
            segment.dataset.revealed = 'true';
            const animation = segment.animate(
                [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0% 0 0)' }],
                { duration: 400, easing: 'ease-out', fill: 'forwards' }
            );
            animation.finished.then(() => {
                segment.style.clipPath = 'none';
                animation.cancel();
            }).catch(() => undefined);
        };

        seriesDefinitions.forEach((definition, seriesIndex) => {
            if (definition.type !== 'line') return;
            // L'ordre des tracés dans le DOM ne suit pas forcément celui des séries :
            // on retrouve la courbe par son index réel pour garder la bonne couleur.
            const path = container.querySelector<SVGPathElement>(
                `.apexcharts-series[data\\:realIndex="${seriesIndex}"] path.apexcharts-line`
            );
            if (!path) return;
            // La géométrie de la courbe change pendant l'animation d'Apex : on
            // recalcule toujours le segment au lieu de réutiliser l'ancien, sinon
            // il reste figé sur un tracé intermédiaire (pointillés et coupure faux).
            const existing = path.parentElement?.querySelector<SVGPathElement>('.conquest-incomplete-segment');
            const wasRevealed = existing?.dataset.revealed === 'true';
            existing?.remove();
            path.style.removeProperty('stroke-dasharray');

            if (definition.data[lastBucketIndex] == null || definition.data[lastBucketIndex - 1] == null) return;
            const firstIndex = definition.data.findIndex((value) => value != null);
            // Pendant l'animation, l'attribut d est un tracé intermédiaire : on mesure
            // le tracé final qu'Apex conserve dans pathTo.
            const finalPath = path.getAttribute('pathTo');
            const geometry = finalPath
                ? document.createElementNS('http://www.w3.org/2000/svg', 'path')
                : path;
            if (geometry !== path) {
                geometry.setAttribute('d', finalPath as string);
                geometry.setAttribute('visibility', 'hidden');
                path.after(geometry);
            }
            const measure = () => {
                const total = geometry.getTotalLength();
                if (!Number.isFinite(total) || total <= 0) return null;
                const startX = geometry.getPointAtLength(0).x;
                const endX = geometry.getPointAtLength(total).x;
                if (endX <= startX || firstIndex < 0 || firstIndex >= lastBucketIndex) return null;

                const penultimateX = endX - (endX - startX) / (lastBucketIndex - firstIndex);
                let low = 0;
                let high = total;
                for (let step = 0; step < 24; step += 1) {
                    const middle = (low + high) / 2;
                    if (geometry.getPointAtLength(middle).x < penultimateX) low = middle;
                    else high = middle;
                }

                // Apex ne permet pas de pointiller seulement un segment d'une courbe.
                // On garde le tracé continu jusqu'à l'avant-dernier point et l'on
                // superpose la dernière portion de courbe sous forme de petits tirets.
                const segmentLength = total - high;
                const samples = Math.max(2, Math.ceil(segmentLength / 2));
                const curve = Array.from({ length: samples + 1 }, (_, sample) => {
                    const point = geometry.getPointAtLength(high + (segmentLength * sample) / samples);
                    return `${sample === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
                });
                return { total, high, curve };
            };
            const measured = measure();
            if (geometry !== path) geometry.remove();
            if (!measured) return;
            const { total, high, curve } = measured;

            path.style.setProperty('stroke-dasharray', `${high} ${total + 10}`, 'important');

            const dotted = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            dotted.setAttribute('class', 'conquest-incomplete-segment');
            dotted.setAttribute('d', curve.join(' '));
            dotted.setAttribute('stroke', definition.color);
            dotted.setAttribute('stroke-width', path.getAttribute('stroke-width') || '2');
            dotted.setAttribute('stroke-linecap', 'round');
            dotted.setAttribute('stroke-dasharray', '3 5');
            dotted.setAttribute('fill', 'none');
            dotted.style.pointerEvents = 'none';
            path.after(dotted);
            if (wasRevealed) {
                dotted.dataset.revealed = 'true';
            } else {
                dotted.style.clipPath = 'inset(0 100% 0 0)';
                if (animateDashes) reveal(dotted);
            }
        });
    };

    const options = useChart({
        chart: {
            // Apex n'applique stackOnlyBar qu'aux graphiques mixtes : sans barre,
            // il empilerait les courbes les unes sur les autres.
            type: 'line', stacked: series.some((item) => item.type === 'bar'), stackType: 'normal', stackOnlyBar: true,
            animations: {
                enabled: true,
                speed: 800,
                animateGradually: { enabled: false },
                dynamicAnimation: { enabled: true, speed: 800 },
            },
            toolbar: { show: false },
            events: {
                mounted: (chartContext: any) => {
                    onChartMounted?.(chartContext);
                    injectLabelMarkers();
                    requestAnimationFrame(() => styleIncompletePeriod(false));
                },
                updated: () => {
                    injectLabelMarkers();
                    requestAnimationFrame(() => styleIncompletePeriod(false));
                },
                animationEnd: () => styleIncompletePeriod(true),
            },
        },
        states: {
            hover: { filter: { type: 'none' } },
            active: { filter: { type: 'none' } },
        },
        colors,
        stroke: { width: series.map((item) => item.type === 'bar' ? 0 : 2), curve: 'smooth', dashArray: seriesDefinitions.map((item) => item.comparisonSeries ? 5 : 0) },
        markers: { size: 3, strokeWidth: 0 },
        plotOptions: { bar: { columnWidth: '48%', borderRadius: 3 } },
        dataLabels: { enabled: false },
        annotations: { points: labelAnnotations },
        xaxis: { categories: points.map((point) => formatDate(point.date, granularity)), tooltip: { enabled: false } },
        yaxis: axisConfig,
        legend: { show: false },
        grid: { borderColor: '#E8EBEE', strokeDashArray: 3, padding: { left: comparison !== 'none' ? 110 : 0 } },
        tooltip: {
            shared: true,
            intersect: false,
            onDatasetHover: { highlightDataSeries: false },
            custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
                const point = points[dataPointIndex];
                if (!point) return '';
                const escape = (value: string) => value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char));
                const detail = point.competitor_visibility_rate;
                const rows = activeMetrics.map((metric) => {
                    if (metric === 'competitor_visibility_rate' && detail) {
                        const breakdown: Array<[string, number, string]> = [
                            ['Organic', detail.organic_only, '#18A155'],
                            ['Comp. Solo bidder', detail.solo_bidder, '#E95954'],
                            ['Comp. Top ad', detail.top_ad_position, '#EF8783'],
                            ['Other Comp.', detail.other_competitors, '#F7C2C0'],
                        ];
                        return `<div style="display:flex;align-items:center;gap:9px;margin:10px 0 4px"><span style="width:5px;height:18px;border-radius:4px;background:#9BA5AE"></span><span>${escape(METRICS[metric].label)}</span></div>${breakdown.map(([label, breakdownValue, color]) => `<div style="display:flex;align-items:center;justify-content:space-between;padding:3px 0 3px 18px;gap:28px"><span style="display:flex;align-items:center;gap:9px"><span style="width:5px;height:16px;border-radius:4px;background:${color}"></span>${escape(label)}</span><span>${formatValue(breakdownValue, 'percent')}</span></div>`).join('')}`;
                    }
                    return `<div style="display:flex;align-items:center;justify-content:space-between;gap:24px;padding:6px 0"><span style="display:flex;align-items:center;gap:9px"><span style="width:5px;height:18px;border-radius:4px;background:${METRICS[metric].color}"></span>${escape(METRICS[metric].label)}</span><strong style="font-weight:500">${formatValue(getValue(point, metric), METRICS[metric].unit)}</strong></div>`;
                }).join('');
                const date = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${point.date}T00:00:00Z`));
                return `<div style="min-width:260px;padding:16px 18px;background:#fff;border:1px solid #E8EBEE;border-radius:12px;font:14px 'Public Sans',sans-serif;color:#333C47;box-shadow:0 5px 15px #00000012"><div style="font-size:16px;color:#637381;margin-bottom:10px">${escape(date)}</div>${rows}</div>`;
            },
        },
    });

    if (loading) {
        return (
            <Box sx={{ width: '100%', minWidth: 0, height: 320, px: 2, pt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                    <Skeleton variant="text" width={150} height={20} />
                    <Skeleton variant="text" width={85} height={20} />
                </Box>
                <Box sx={{ height: 250, display: 'flex', gap: 2 }}>
                    <Box sx={{ width: 48, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        {[0, 1, 2, 3, 4].map((index) => <Skeleton key={index} variant="text" width={38} height={14} />)}
                    </Box>
                    <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', borderBottom: '1px solid #E8EBEE', pb: 1 }}>
                        {[56, 72, 49, 87, 64].map((height, index) => (
                            <Box key={index} sx={{ width: '12%', height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                                <Skeleton variant="rectangular" animation="wave" sx={{ width: '100%', height: `${height}%`, borderRadius: '4px 4px 0 0' }} />
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ width: 48, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        {[0, 1, 2, 3, 4].map((index) => <Skeleton key={index} variant="text" width={38} height={14} />)}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', ml: '64px', mr: '64px', mt: 0.5 }}>
                    {[0, 1, 2, 3, 4].map((index) => <Skeleton key={index} variant="text" width={58} height={16} />)}
                </Box>
            </Box>
        );
    }

    if (!points.length || !activeMetrics.length) {
        return <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography color="text.secondary">No chart data available</Typography></Box>;
    }

    return (
        <Box ref={chartContainerRef} sx={{ width: '100%', minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 2, py: 0.5, pr: 1 }}>
                {activeMetrics.map((metric) => (
                    <Box key={metric} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Box sx={{ height: '6px', width: '6px', aspectRatio: '1 / 1', flexShrink: 0, borderRadius: '50%', bgcolor: METRICS[metric].color }} />
                        <Typography sx={{ color: '#161C24', fontFamily: 'Public Sans', fontSize: '13px', fontStyle: 'normal', fontWeight: 500, lineHeight: '22px' }}>
                            {METRICS[metric].label}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Chart key={`${comparison}-${activeMetrics.join('-')}`} type="line" series={series} options={options} height={320} />
            {comparison !== 'none' && comparisonValues.length > 0 && (
                <Box
                    ref={comparisonContainerRef}
                    sx={{ position: 'relative', width: '100%', height: `${Math.max(activeMetrics.length, 1) * 17}px`, mt: '-2px', mb: 1 }}
                >
                    <Typography
                        data-export-comparison-period
                        sx={{ position: 'absolute', left: '8px', top: 0, width: 110, whiteSpace: 'pre-line', color: '#919EAB', fontFamily: 'Public Sans', fontSize: '11px', lineHeight: '120%' }}
                    >
                        {comparisonPeriodLabel}
                    </Typography>
                    {comparisonValues.map((values, pointIndex) => (
                        <Stack
                            key={`${points[pointIndex].date}-comparison`}
                            data-export-comparison-index={pointIndex}
                            ref={(element: HTMLDivElement | null) => { comparisonItemRefs.current[pointIndex] = element; }}
                            spacing="2px"
                            alignItems="center"
                            sx={{ position: 'absolute', top: 0, left: 0, transform: 'translateX(-50%)', minWidth: '76px' }}
                        >
                            {values.map(({ metric, color, value }) => {
                                const display = value === null ? '—' : value > 999 ? '>999%' : value < -999 ? '<-999%' : `${value > 0 ? '+' : ''}${Math.round(value)}%`;
                                return (
                                    <Box key={metric} data-export-comparison-row={metric} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', minHeight: '14px' }}>
                                        {pointIndex === 0 && (
                                            <Box sx={{ width: 8, height: 8, bgcolor: color, borderRadius: '50%', flexShrink: 0, mr: 1 }} />
                                        )}
                                        <Typography sx={{ textAlign: 'center', fontFamily: 'Public Sans', fontSize: '12px', fontWeight: 400, lineHeight: '120%', fontVariantNumeric: 'tabular-nums', color: value === null || value === 0 ? '#919EAB' : value > 0 ? '#22A06B' : '#FF5630' }}>
                                            {display}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    ))}
                </Box>
            )}
        </Box>
    );
}